import Layout2 from "@/components/Layout/Layout2";
import LeftBar2 from "../../../../components/Layout/LeftBar2";
import { MapPin, Phone, Mail, Calendar, Globe, MapPinned } from "lucide-react";
import { blackUser } from "@/assets";
import React from "react";
import { useEffect, useState } from "react";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { backofficeLinks } from "../../../../components/lib/SidebarLinks";
import { Separator } from "@/components/ui/separator";
import Payroll from "@/components/backoffice/all-staff/staff-profile/payroll";
import Report from "@/components/backoffice/all-staff/staff-profile/Report";
import Shift from "@/components/backoffice/all-staff/staff-profile/shift";
import Leaves from "@/components/backoffice/all-staff/staff-profile/leaves";
import Transfer from "@/components/backoffice/all-staff/staff-profile/transfer";
import Attendance from "@/components/backoffice/all-staff/staff-profile/attendance";
import StaffPermissionTab from "@/components/backoffice/all-staff/staff-profile/staff-permission-tab";
import {
  getSingleStaff,
  staffSchedule,
} from "@/api/new-api/staff-management/staff/staff";
import { getStaffLeavesById } from "@/api/new-api/staff-management/leave/leave";
import { Link, useParams, useLocation } from "react-router-dom";
import Loading from "@/components/common/loading";
import { useDispatch } from "react-redux";
import hasPermission from "@/hooks/hasPermission";

function StaffProfile() {
  const { id } = useParams();
  const dispatch = useDispatch();
  const location = useLocation();

  const staffTabs = React.useMemo(
    () => [
      {
        id: "payroll",
        label: "Payroll",
        permission: "back_office.staff_management.view_staff_payroll",
      },
      {
        id: "report",
        label: "Attendance & Report",
        permission: "back_office.staff_management.view_permissionand_report",
      },
      {
        id: "shift",
        label: "Shift",
        permission: "back_office.staff_management.view_shift",
      },
      {
        id: "leaves",
        label: "Leaves",
        permission: "back_office.staff_management.view_leaves",
      },
      {
        id: "permission",
        label: "Permission",
        permission: "back_office.staff_management.view_permission",
      },
    ],
    []
  );

  const allowedTabs = React.useMemo(
    () => staffTabs.filter((tab) => hasPermission(tab.permission)),
    [staffTabs]
  );

  // Determine initial tab based on state passed via react-router Link and user permissions
  const initialTab = React.useMemo(() => {
    const requested = location.state?.activeTab;
    if (requested && allowedTabs.some((t) => t.id === requested)) {
      return requested;
    }
    return allowedTabs[0]?.id || "payroll";
  }, [location.state?.activeTab, allowedTabs]);

  // If a specific activeTab was passed in location state, force update the tabs storage
  if (location.state?.activeTab) {
    try {
      const stored = JSON.parse(localStorage.getItem("tabs-data")) || {};
      if (!stored["staff-manage-staff-admin"]) {
        stored["staff-manage-staff-admin"] = {};
      }
      if (stored["staff-manage-staff-admin"]["default"] !== location.state.activeTab) {
        stored["staff-manage-staff-admin"]["default"] = location.state.activeTab;
        localStorage.setItem("tabs-data", JSON.stringify(stored));
      }
    } catch (e) {
      console.error("Failed to update tabs localStorage:", e);
    }
  }

  const [activeTab, setActiveTab] = useState(initialTab);

  useEffect(() => {
    if (allowedTabs.length > 0) {
      if (!allowedTabs.some((t) => t.id === activeTab)) {
        setActiveTab(allowedTabs[0]?.id);
      }
    }
  }, [allowedTabs, activeTab]);
  const [singleStaff, setSingleStaff] = useState("");
  const [staffLeaves, setStaffLeaves] = useState([]);
  const [staffSchedules, setStaffSchedules] = useState({});
  const [loading, setLoading] = useState(false);
  const [leavesLoading, setLeavesLoading] = useState(false);

  useEffect(() => {
    if (!id) return;

    const fetchSingleStaff = async () => {
      setLoading(true);
      try {
        const staffRes = await getSingleStaff(id);
        if (staffRes?.status === "success") {
          setSingleStaff(staffRes.data?.staff || null);
        }
      } catch (error) {
        console.error("Failed to fetch staff data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchSingleStaff();
  }, [id]);

  useEffect(() => {
    if (!id || activeTab !== "leaves") return;

    const fetchLeaves = async () => {
      setLeavesLoading(true);
      try {
        const leaveRes = await getStaffLeavesById(id);
        if (leaveRes?.status === "success") {
          setStaffLeaves(leaveRes.data?.leaves || []);
        }
      } catch (error) {
        console.error("Failed to fetch leaves:", error);
      } finally {
        setLeavesLoading(false);
      }
    };

    fetchLeaves();
  }, [id, activeTab]);

  useEffect(() => {
    if (!id || activeTab !== "shift") return;

    const fetchSchedule = async () => {
      try {
        const scheduleRes = await staffSchedule(id);
        if (scheduleRes?.status === "success") {
          setStaffSchedules(scheduleRes.data || {});
        }
      } catch (error) {
        console.error("Failed to fetch schedule:", error);
      }
    };

    fetchSchedule();
  }, [id, activeTab]);

  if (loading) {
    return <Loading links={backofficeLinks} />;
  }

  return (
    <Layout2
      LeftBar={<LeftBar2 sidebarLinks={backofficeLinks} />}
      pageName={{ icon: true, title: `Staff ID: ${singleStaff?.unique_id || singleStaff?.id || ""}` }}
      paths={[
        { name: "Back Office", path: "/backoffice" },
        { name: "Staff Management", path: "/backoffice/staffManagement" },
        { name: "All Staff", path: "/backoffice/staff-management/staff/all" },
        {
          name: "Staff Details",
          path: "/backoffice/staff-management/staff/profile",
        },
      ]}
    >
      <div className="w-full flex flex-col lg:flex-row gap-6 py-5 px-4 md:px-6">
        {/* Left Side Profile */}
        <Card className="border border-neutral-700 w-full lg:w-80 shrink-0">
          <CardHeader>
            <div className="flex flex-col gap-4 w-full">
              <div className="flex justify-between items-start w-full">
                <Avatar className="w-20 h-20 rounded-md shrink-0">
                  <AvatarImage
                    className="h-full w-full object-cover"
                    src={singleStaff?.file_manager_url || singleStaff?.photo || blackUser}
                    alt={singleStaff?.user?.first_name}
                  />
                </Avatar>
                {singleStaff?.user?.status === "active" ? (
                  <span className="text-cgreen flex items-center gap-1 text-xs shrink-0 mt-1">
                    <span className="text-[8px]">🟢 </span>Active
                  </span>
                ) : (
                  <span className="text-cred flex items-center gap-1 text-xs shrink-0 mt-1">
                    <span className="text-[8px]">🔴 </span>Inactive
                  </span>
                )}
              </div>
              <div>
                <h2 className="text-xl text-cwhite font-bold break-words">
                  {singleStaff?.user?.first_name}{" "}
                  {singleStaff?.user?.last_name}
                </h2>
                <p className="text-sm text-cwhite3 break-words">
                  {singleStaff?.designation}
                </p>
                {hasPermission("back_office.staff_management.staff_update") && (
                  <Link
                    to={`/backoffice/staff-management/staff/update/${singleStaff?.id}`}
                  >
                    <Button className="mt-2 bg-cyellow text-black font-bold px-10 w-full" size="sm">
                      Edit
                    </Button>
                  </Link>
                )}
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4 text-sm">
            <div>
              <h2 className="text-white font-medium mb-1">Basic Info</h2>
              <Separator />
            </div>
            <div className="flex flex-col gap-4 text-cwhite3">
              <div className="flex justify-between items-start gap-2">
                <strong className="flex gap-1 items-center shrink-0">
                  <MapPin size={17} /> Location
                </strong>{" "}
                <span className="text-right break-words text-xs max-w-[65%] text-cwhite3">
                  {singleStaff?.address || "Not specified"}
                </span>
              </div>
              <div className="flex justify-between items-center gap-2">
                <strong className="flex gap-1 items-center shrink-0">
                  <Phone size={17} /> Phone
                </strong>{" "}
                <span className="text-right break-all text-xs max-w-[65%] text-cwhite3">
                  {singleStaff?.phone || "Not specified"}
                </span>
              </div>
              <div className="flex justify-between items-center gap-2">
                <strong className="flex gap-1 items-center shrink-0">
                  <Mail size={17} /> Email
                </strong>{" "}
                <span className="text-right break-all text-xs max-w-[65%] text-cwhite3">
                  {singleStaff?.user?.email || "Not specified"}
                </span>
              </div>
              <div className="flex justify-between items-center gap-2">
                <strong className="flex gap-1 items-center shrink-0">
                  <Calendar size={17} /> Join Date
                </strong>{" "}
                <span className="text-right break-words text-xs max-w-[65%] text-cwhite3">
                  {singleStaff?.joining_date || "Not specified"}
                </span>
              </div>
              <div className="flex justify-between items-center gap-2">
                <strong className="shrink-0">Gender</strong>{" "}
                <span className="text-right break-words text-xs max-w-[65%] text-cwhite3">
                  {singleStaff?.gender || "Not specified"}
                </span>
              </div>
              <div className="flex justify-between items-center gap-2">
                <strong className="shrink-0">Branch</strong>{" "}
                <span className="text-right break-words text-xs max-w-[65%] text-cwhite3">
                  {singleStaff?.branch_name || "Not specified"}
                </span>
              </div>
              <div className="flex justify-between items-center gap-2">
                <strong className="shrink-0">Staff Type</strong>{" "}
                <span className="text-right break-words text-xs text-cyellow capitalize max-w-[65%]">
                  {singleStaff?.employment_type || "Not specified"}
                </span>
              </div>
            </div>

            <div>
              <h2 className="text-white font-medium pt-2 mb-1">Bank Information</h2>
              <Separator />
            </div>
            <div className="flex flex-col gap-4 text-cwhite3">
              <div className="flex justify-between items-center gap-2 capitalize">
                <strong className="shrink-0">Salary Type</strong>{" "}
                <span className="text-right break-words text-xs max-w-[65%] text-cwhite3">
                  {singleStaff?.salary_type || "Not specified"}
                </span>
              </div>
              <div className="flex justify-between items-center gap-2">
                <strong className="shrink-0">Account No</strong>{" "}
                <span className="text-right break-all text-xs max-w-[65%] text-cwhite3">
                  {singleStaff?.bank_account_number || "Not specified"}
                </span>
              </div>
              <div className="flex justify-between items-center gap-2">
                <strong className="shrink-0">Bank</strong>{" "}
                <span className="text-right break-words text-xs max-w-[65%] text-cwhite3">
                  {singleStaff?.bank_name || "Not specified"}
                </span>
              </div>
              <div className="flex justify-between items-center gap-2">
                <strong className="shrink-0">Bank Branch</strong>{" "}
                <span className="text-right break-words text-xs max-w-[65%] text-cwhite3">
                  {singleStaff?.bank_branch_name || "Not specified"}
                </span>
              </div>
              <div className="flex justify-between items-center gap-2">
                <strong className="shrink-0">Currency</strong>{" "}
                <span className="text-right break-words text-xs max-w-[65%] text-cwhite3">
                  {singleStaff?.currency || "Not specified"}
                </span>
              </div>
            </div>

            <div>
              <h2 className="text-white font-medium pt-2 mb-1">Others Info</h2>
              <Separator />
            </div>
            <div className="flex flex-col gap-4 text-cwhite3">
              <div className="flex justify-between items-center gap-2 capitalize">
                <strong className="flex gap-1 items-center shrink-0">
                  <Globe size={17} /> Language
                </strong>{" "}
                <span className="text-right break-words text-xs max-w-[65%] text-cwhite3">
                  {singleStaff?.language || "Not specified"}
                </span>
              </div>
              <div className="flex justify-between items-center gap-2 capitalize">
                <strong className="flex gap-1 items-center shrink-0">
                  <MapPinned size={17} /> Country
                </strong>{" "}
                <span className="text-right break-words text-xs max-w-[65%] text-cwhite3">
                  {singleStaff?.country || "Not specified"}
                </span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Right Side Tabs */}

        <Card className="bg-transparent flex-1 min-w-0">
          <Tabs
            valueKey="staff-manage-staff-admin"
            className="bg-transparent relative"
            defaultValue={initialTab}
            onValueChange={setActiveTab}
          >
            <TabsList className="flex h-auto w-full overflow-x-auto justify-start rounded-none border-b border-neutral-700 bg-transparent p-0">
              {hasPermission("back_office.staff_management.view_staff_payroll") && (
                <TabsTrigger
                  className="flex-shrink-0 rounded-none border-b-2 border-transparent px-4 py-2 text-sm font-medium data-[state=active]:border-cyellow data-[state=active]:bg-transparent data-[state=active]:text-cyellow"
                  value="payroll"
                >
                  Payroll
                </TabsTrigger>
              )}
              {hasPermission("back_office.staff_management.view_attendance_and_report") && (
                <TabsTrigger
                  className="flex-shrink-0 rounded-none border-b-2 border-transparent px-4 py-2 text-sm font-medium data-[state=active]:border-cyellow data-[state=active]:bg-transparent data-[state=active]:text-cyellow"
                  value="report"
                >
                  Attendance & Report
                </TabsTrigger>
              )}
              {hasPermission("back_office.staff_management.view_shift") && (
                <TabsTrigger
                  className="flex-shrink-0 rounded-none border-b-2 border-transparent px-4 py-2 text-sm font-medium data-[state=active]:border-cyellow data-[state=active]:bg-transparent data-[state=active]:text-cyellow"
                  value="shift"
                >
                  Shift
                </TabsTrigger>
              )}
              {hasPermission("back_office.staff_management.view_leaves") && (
                <TabsTrigger
                  className="flex-shrink-0 rounded-none border-b-2 border-transparent px-4 py-2 text-sm font-medium data-[state=active]:border-cyellow data-[state=active]:bg-transparent data-[state=active]:text-cyellow"
                  value="leaves"
                >
                  Leaves
                </TabsTrigger>
              )}
              {hasPermission("back_office.staff_management.view_permission") && (
                <TabsTrigger
                  className="flex-shrink-0 rounded-none border-b-2 border-transparent px-4 py-2 text-sm font-medium data-[state=active]:border-cyellow data-[state=active]:bg-transparent data-[state=active]:text-cyellow"
                  value="permission"
                >
                  Permission
                </TabsTrigger>
              )}
            </TabsList>
            {activeTab === "payroll" &&
              hasPermission("back_office.staff_management.view_staff_payroll") && <Payroll />}
            {activeTab === "report" &&
              hasPermission("back_office.staff_management.view_permissionand_report") && <Report />}
            {activeTab === "attendance" && <Attendance />}
            {activeTab === "shift" &&
              hasPermission("back_office.staff_management.view_shift") && (
                <Shift staffSchedules={staffSchedules} />
              )}
            {activeTab === "leaves" &&
              hasPermission("back_office.staff_management.view_leaves") && (
                <Leaves data={staffLeaves} loading={leavesLoading} />
              )}
            {activeTab === "permission" &&
              hasPermission("back_office.staff_management.view_permission") && (
                <StaffPermissionTab />
              )}
            {/* <Transfer /> */}
          </Tabs>
        </Card>
      </div>
    </Layout2>
  );
}

export default StaffProfile;
